import { type FC, type ReactNode } from 'react';
import { Platform, Pressable, StyleSheet, View, type ViewStyle } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Stack } from '@/components/layout';
import Modal from '@/components/ui/modal/modal';
import { SheetContent, SheetModal } from '@/components/ui/sheet-modal';
import { SvgIcon } from '@/components/ui/svg-icon';
import { Text } from '@/components/ui/text';
import { createStyles, useScaling } from '@/hooks';
import { colors } from '@/theme/colors';

export type WheelPickerSheetProps = {
  open: boolean;
  /** Called for every close path (X / backdrop / back / drag) — i.e. the cancel/discard path. */
  onOpenChange: (open: boolean) => void;
  /** Title shown centered in the header. */
  title?: string;
  /** Wired to the header check button: commit the temp selection, then close. */
  onConfirm: () => void;
  /** Extra style merged into the phone bottom-sheet container (e.g. override corner radius). */
  sheetStyle?: ViewStyle | ViewStyle[];
  /**
   * Draw the rounded, inset "selected row" pill behind the wheel (Android only — iOS renders its
   * own native pill). Enable for pickers whose native selection band has been turned off.
   */
  showSelectionPill?: boolean;
  /** The wheel (`Picker` / `DatePicker`) rendered below the header. */
  children: ReactNode;
};

/**
 * Bottom-sheet shell for a wheel picker. Renders an `X / title / ✓` header above the
 * wheel: the check commits (`onConfirm`), while X / backdrop / back / drag discard via
 * `onOpenChange(false)`. The caller owns the temp value and only commits on `onConfirm`.
 */
export const WheelPickerSheet: FC<WheelPickerSheetProps> = ({
  open,
  onOpenChange,
  title,
  onConfirm,
  sheetStyle,
  showSelectionPill = false,
  children,
}) => {
  const { scale, isTablet } = useScaling();
  const styles = useStyles();
  const insets = useSafeAreaInsets();

  // Rounded, inset highlight behind the centered (selected) row — matches the iOS native pill.
  const wheel = (
    <View style={styles.wheelWrap}>
      {showSelectionPill && Platform.OS === 'android' ? (
        <View pointerEvents="none" style={styles.selectionPill} />
      ) : null}
      {children}
    </View>
  );

  // Shared `X / title / ✓` header — identical for the sheet and the tablet popup.
  const header = (
    <Stack
      direction="row"
      align="center"
      justify="space-between"
      paddingX={scale(20)}
      paddingY={scale(16)}>
      <Pressable onPress={() => onOpenChange(false)} hitSlop={8}>
        <SvgIcon name="IcXClose" size={scale(24)} color={colors['10']} />
      </Pressable>
      {title ? (
        <Text variant="Font Guardian App/Heading/Heading H6 18px" style={styles.title}>
          {title}
        </Text>
      ) : (
        <Stack />
      )}
      <Pressable onPress={onConfirm} hitSlop={8}>
        <SvgIcon name="IcCheckMd" size={scale(24)} color={colors.Blue} />
      </Pressable>
    </Stack>
  );

  // Tablet: a centered popup card (same header + wheel), instead of a bottom sheet.
  if (isTablet) {
    return (
      <Modal visible={open}>
        <View style={styles.popupCard}>
          {header}
          <Stack paddingBottom={scale(16)}>{wheel}</Stack>
        </View>
      </Modal>
    );
  }

  return (
    <SheetModal open={open} onOpenChange={onOpenChange}>
      <SheetContent style={StyleSheet.flatten([styles.sheet, sheetStyle])}>
        {header}
        <Stack paddingBottom={insets.bottom + scale(8)}>{wheel}</Stack>
      </SheetContent>
    </SheetModal>
  );
};

const useStyles = createStyles(({ scale, deviceWidth }) => ({
  sheet: {},
  title: {
    color: colors['10'],
  },
  popupCard: {
    width: Math.min(scale(380), deviceWidth * 0.8),
    backgroundColor: 'white',
    borderRadius: scale(20),
    overflow: 'hidden',
  },
  wheelWrap: {
    position: 'relative',
    // Center a narrower-than-full wheel (e.g. the date picker's 80% width) horizontally.
    alignItems: 'center',
  },
  // Centered on the wheel; height ≈ one row. Inset + rounded to mirror the iOS selection pill.
  selectionPill: {
    position: 'absolute',
    left: scale(16),
    right: scale(16),
    top: '50%',
    height: scale(48),
    transform: [{ translateY: -scale(24) }],
    borderRadius: scale(16),
    borderCurve: 'continuous',
    backgroundColor: colors['2'],
  },
}));

export default WheelPickerSheet;
